// Pure data and helper logic for POSDashBoard - no React or JSX dependencies.

export const statusFilterOptions = [
    { value: "OK", label: "OK" },
    { value: "IsOffline", label: "Offline Clients" },
    { value: "InvoiceSyncPending", label: "Invoice Sync Pending" },
    { value: "DeleteSyncPending", label: "Delete Sync Pending" },
    { value: "VersionMismatch", label: "Version Mismatch" },
];

const hiddenBadgeFlags = [
    "OFFLINE",
    "INVOICE SYNC PENDING",
    "DELETE SYNC PENDING",
    "ERP INVOICE DATE MISMATCH",
    "ERP DELETE NOT SYNCED",
];

export function formatVersionMismatchLabel(flag) {
    const isService = (flag || "").toUpperCase().startsWith("SERVICE");
    return isService ? "Service Version Mismatch" : "POS Version Mismatch";
}

export function getVisibleBadges(versionMismatchList = []) {
    return versionMismatchList.filter(
        status => !hiddenBadgeFlags.includes(status) && !status.includes("VERSION MISMATCH")
    );
}

export function formatOfflineDuration(lastSeenAt) {
    if (!lastSeenAt) return "";
    const diffMs = Date.now() - new Date(lastSeenAt).getTime();
    if (diffMs < 0) return "";

    const minutes = Math.floor(diffMs / 60000);
    if (minutes < 60) return `${minutes}m`;

    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ${minutes % 60}m`;

    const days = Math.floor(hours / 24);
    return `${days}d ${hours % 24}h`;
}

export function formatAgo(dateStr) {
    if (!dateStr) return "-";
    const duration = formatOfflineDuration(dateStr);
    return duration ? `${duration} ago` : "-";
}



export function normalizeText(text = "") {
    return text
        .toString()
        .toLowerCase()
        .replace(/[\s()\-_.]/g, "");
}

export function formatForSearch(dateStr) {
    if (!dateStr) return "";
    const d = new Date(dateStr);
    return (
        d.toLocaleDateString("en-GB") + " " +
        d.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" })
    );
}

// No longer used by isBillNumberMismatch (kept in case other code imports it).
export function isSameInvoiceNo(posNo, erpNo) {
    if (!posNo || !erpNo) return false;
    return posNo.toString().trim() === erpNo.toString().trim();
}

export function isUpdateRequired(running, current) {
    if (!running || !current) return false;
    return running !== current;
}

export function isDateMismatch(posDate, erpDate, allowedMinutes) {
    if (!posDate) return false;
    if (!erpDate) return true;

    const limit =
        allowedMinutes === "" ||
            allowedMinutes === null ||
            allowedMinutes === undefined
            ? 10
            : Number(allowedMinutes);

    const posTime = new Date(posDate).getTime();
    const erpTime = new Date(erpDate).getTime();
    const diffMinutes = Math.abs(posTime - erpTime) / (1000 * 60);

    return diffMinutes > limit;
}

export function formatDateTimeShort(dateStr) {
    if (!dateStr) return "-";
    const d = new Date(dateStr);
    return d.toLocaleDateString("en-GB") + " " +
        d.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" });
}

// `tabMode` selects which slice of the data is shown: "attention" (default)
// = only rows that need a look, "ok" = only healthy rows, "offline" = only
// offline rows, "all" = everything. The Status dropdown is gone, so this is
// tab-driven only now.
export function applyStatusFilters(row, hasSearched, searchText, allRecordsOK, tabMode = "attention") {
    const isOK = Number(row.IsOk) === 1;
    const isOffline = Number(row.IsOffline) === 1;

    if (tabMode === "all") return true;
    if (tabMode === "ok") return isOK;
    if (tabMode === "offline") return isOffline;
    // "attention" (default): NOT offline AND NOT ok — i.e. (Total - Offline) - OK
    return !isOK && !isOffline;
}

export function buildSearchText(row, partyNameMap) {
    const parts = [];

    if (row.Party) {
        parts.push(partyNameMap[row.Party] || "");
    }

    const invoiceMismatch = isDateMismatch(
        row.MaxInvoiceCreatedOn,
        row.ERPMaxInvoiceCreatedOn,
        row?.SyncTime ?? 10
    );

    parts.push(
        row.MaxInvoiceCreatedOn,
        row.ERPMaxInvoiceCreatedOn,
        formatForSearch(row.MaxInvoiceCreatedOn),
        formatForSearch(row.ERPMaxInvoiceCreatedOn),
        row.MaxInvoiceFullInvoiceNo,
        row.ERPMaxInvoiceFullInvoiceNo,
    );
    if (invoiceMismatch && Number(row.IsServerMachine) === 1) {
        parts.push("ERP Invoice Date Mismatch");
    }

    const deleteMismatch = isDateMismatch(
        row.MaxDeletedInvoiceCreatedOn,
        row.ERPDeleteMaxInvoiceCreatedOn,
        row?.SyncTime ?? 10
    );

    parts.push(
        row.MaxDeletedInvoiceCreatedOn,
        row.ERPDeleteMaxInvoiceCreatedOn,
        formatForSearch(row.MaxDeletedInvoiceCreatedOn),
        formatForSearch(row.ERPDeleteMaxInvoiceCreatedOn),
        row.MaxDeletedInvoiceFullInvoiceNo,
        row.ERPDeleteMaxInvoiceFullInvoiceNo,
    );
    if (deleteMismatch && Number(row.IsServerMachine) === 1) {
        parts.push("ERP Delete not synced");
    }

    if (row.MacAddress) parts.push(row.MacAddress);
    if (row.VersionMismatchList?.length) {
        parts.push(...row.VersionMismatchList);
    }

    if (row.Versions?.length) {
        row.Versions.forEach(v => {
            parts.push(
                v.label,
                "Running", v.running,
                "Latest", v.current
            );
            if (isUpdateRequired(v.running, v.current)) {
                parts.push("Update Required");
            }
        });
    }

    if (row.CreatedOnList?.length) {
        parts.push(...row.CreatedOnList);
    }

    Object.values(row).forEach(v => {
        if (v !== null && v !== undefined && typeof v !== "object") {
            parts.push(v.toString());
        }
    });

    return normalizeText(parts.join(" "));
}

export function getUniqueVersionOptions(data = []) {
    const uniqueVersions = new Set();

    data.forEach(row => {
        row.Versions?.forEach(v => {
            if (v.current) {
                uniqueVersions.add(v.current);
            }
        });
    });

    return Array.from(uniqueVersions)
        .sort()
        .map(v => ({ value: v, label: v }));
}

export function applyVersionFilter(row, versionFilter) {
    if (!versionFilter) return true;
    return row.Versions?.some(v => v.running === versionFilter.value) ?? false;
}


export const attentionSubTabOptions = [
    { key: "serviceNotWorking", label: "Service Not Working" },
    { key: "billNumberWise", label: "Bill Number Wise" },
    { key: "billTimeWise", label: "Bill Time Wise" },
    { key: "versionMismatch", label: "Version Mismatch" },
];

function isNotOkStatus(status) {
    return !!status && status.toString().toUpperCase() !== "OK";
}

function getPosDashboardSyncFrequencyMinutes(row) {
    return Number(row?.POSDasboardSyncFrequency) || 1440;
}

// "Service Not Working" mirrors the saga's B3 service heartbeat rule:
// a qualifying SERVICE/IsService log is OK only when
// Date.now() - LastSyncDateTime <= POSDasboardSyncFrequency minutes.

export function getServiceNotWorkingEntry(row) {
    const now = Date.now();
    const syncFrequencyMinutes = getPosDashboardSyncFrequencyMinutes(row);

    return (row.Versions || []).find(v => {
        if (!(v.clientType === "SERVICE" && v.isService === true)) return false;
        if (!v.lastSync) return true;

        const lastSyncTime = new Date(v.lastSync).getTime();
        if (!Number.isFinite(lastSyncTime)) return true;

        const diffMs = now - lastSyncTime;
        return diffMs > syncFrequencyMinutes * 60 * 1000;
    });
}

export function isServiceNotWorking(row) {
    return !!getServiceNotWorkingEntry(row);
}
// "Bill Number Wise" — server machines only. Straight string comparison of
// POS's invoice/delete number against ERP's. Equal (including both empty)
// => not shown. Any difference, including one side missing while the other
// has a value, counts as "not same" and appears in this tab.
export function normalizeInvoiceNo(v) {
    return (v ?? "").toString().trim();
}

export function isBillNumberMismatch(row) {
    if (Number(row.IsServerMachine) !== 1) return false;

    const invoiceNoMismatch =
        normalizeInvoiceNo(row.MaxInvoiceFullInvoiceNo) !==
        normalizeInvoiceNo(row.ERPMaxInvoiceFullInvoiceNo);

    const deletedInvoiceNoMismatch =
        normalizeInvoiceNo(row.MaxDeletedInvoiceFullInvoiceNo) !==
        normalizeInvoiceNo(row.ERPDeleteMaxInvoiceFullInvoiceNo);

    return invoiceNoMismatch || deletedInvoiceNoMismatch;
}

// Picks whichever of two timestamps is more recent; falls back to whichever
// one exists if only one side has a value.
export function getLatestTime(posTime, serviceTime) {
    if (!posTime && !serviceTime) return null;
    if (!posTime) return serviceTime;
    if (!serviceTime) return posTime;
    return new Date(posTime).getTime() >= new Date(serviceTime).getTime()
        ? posTime
        : serviceTime;
}

function getSyncMinutes(row) {
    return Number(row?.SyncTime) || 5;
}

export function isWithinSyncFrequency(posDate, erpDate, thresholdMinutes) {
    if (!posDate) return true;
    if (!erpDate) return false;

    const diffMinutes = Math.abs(new Date(posDate).getTime() - new Date(erpDate).getTime()) / (1000 * 60);
    return diffMinutes < thresholdMinutes;
}

export function isSyncFrequencyMismatch(posDate, erpDate, thresholdMinutes) {
    return !isWithinSyncFrequency(posDate, erpDate, thresholdMinutes);
}

export function getBillTimeMismatchFlags(row) {
    if (Number(row.IsServerMachine) !== 1) {
        return {
            invoiceGapMismatch: false,
            deleteGapMismatch: false,
            latestInvoiceTime: null,
            latestDeleteTime: null,
        };
    }

    const allowedGapMinutes = getSyncMinutes(row) + 2;

    // POS time vs ERP time — inverse of the saga's B4/B5
    // isWithinSyncFrequencyLocal check, including the +2 minute tolerance
    // and the strict boundary (diff must be < threshold to be OK).
    const invoiceGapMismatch = isSyncFrequencyMismatch(
        row.MaxInvoiceCreatedOn,
        row.ERPMaxInvoiceCreatedOn,
        allowedGapMinutes
    );
    const deleteGapMismatch = isSyncFrequencyMismatch(
        row.MaxDeletedInvoiceCreatedOn,
        row.ERPDeleteMaxInvoiceCreatedOn,
        allowedGapMinutes
    );

    return {
        invoiceGapMismatch,
        deleteGapMismatch,
        latestInvoiceTime: row.MaxInvoiceCreatedOn,
        latestDeleteTime: row.MaxDeletedInvoiceCreatedOn,
    };
}

// "Bill Time Wise" — server machines only. For invoice-created and for
// delete separately: take whichever of POS's or Service's own timestamp is
// more recent, then compare THAT time against the current time on the
// machine. If the gap exceeds SyncTime minutes, it's flagged.
export function isBillTimeMismatch(row) {
    const { invoiceGapMismatch, deleteGapMismatch } = getBillTimeMismatchFlags(row);

    return invoiceGapMismatch || deleteGapMismatch;
}

export function isVersionMismatchRow(row) {
    return (row.VersionMismatchList || []).some(s => s.includes("VERSION MISMATCH"));
}

// Per-sub-tab counts for the "Needs Attention" tab.
// - serviceNotWorking is counted from the SERVICE-only rows (its own
//   self-contained list, same one the sub-tab itself renders from).
// - The other three are counted from the "attention pool" — rows that
//   are NOT offline and NOT OK — same pool the sub-tab filters apply to.
// A machine can match more than one sub-tab (e.g. version mismatch AND
// bill time mismatch), so these counts can overlap; the sum is a total
// of "sub-tab hits", not a distinct machine count.
export function getAttentionSubTabCounts(attentionPoolRows = [], serviceOnlyRows = []) {
    return {
        serviceNotWorking: serviceOnlyRows.filter(isServiceNotWorking).length,
        billNumberWise: attentionPoolRows.filter(isBillNumberMismatch).length,
        billTimeWise: attentionPoolRows.filter(isBillTimeMismatch).length,
        versionMismatch: attentionPoolRows.filter(isVersionMismatchRow).length,
    };
}


// Dispatcher for the sub-tab bar. Only meaningful under the "attention" tab.
export function applyAttentionSubTab(row, subTab) {
    if (!subTab || subTab === "all") return true;

    switch (subTab) {
        case "serviceNotWorking":
            return isServiceNotWorking(row);
        case "billNumberWise":
            return isBillNumberMismatch(row);
        case "billTimeWise":
            return isBillTimeMismatch(row);
        case "versionMismatch":
            return isVersionMismatchRow(row);
        default:
            return true;
    }
}

export function getBillNumberMismatchChips(row) {
    if (!isBillNumberMismatch(row)) return [];

    const chips = [];

    if (normalizeInvoiceNo(row.MaxInvoiceFullInvoiceNo) !== normalizeInvoiceNo(row.ERPMaxInvoiceFullInvoiceNo)) {
        const pos = row.MaxInvoiceFullInvoiceNo || "—";
        const erp = row.ERPMaxInvoiceFullInvoiceNo || "—";
        chips.push({
            text: "Invoice Number Mismatch",
            detail: `POS: ${pos}  ·  ERP: ${erp}`,
            tone: "danger",
        });
    }

    if (normalizeInvoiceNo(row.MaxDeletedInvoiceFullInvoiceNo) !== normalizeInvoiceNo(row.ERPDeleteMaxInvoiceFullInvoiceNo)) {
        const pos = row.MaxDeletedInvoiceFullInvoiceNo || "—";
        const erp = row.ERPDeleteMaxInvoiceFullInvoiceNo || "—";
        chips.push({
            text: "Delete Number Mismatch",
            detail: `POS: ${pos}  ·  ERP: ${erp}`,
            tone: "danger",
        });
    }

    return chips;
}
export const getBillNumberMismatchChip = getBillNumberMismatchChips;

export function getBillTimeMismatchChips(row) {
    const {
        invoiceGapMismatch,
        deleteGapMismatch,
        latestInvoiceTime,
        latestDeleteTime,
    } = getBillTimeMismatchFlags(row);

    const chips = [];

    if (invoiceGapMismatch) {
        chips.push({
            text: "Invoice Not Synced",
            detail: `${formatAgo(latestInvoiceTime)}  ·  ${formatDateTimeShort(latestInvoiceTime)}`,
            tone: "warning",
            title: formatDateTimeShort(latestInvoiceTime),
        });
    }
    if (deleteGapMismatch) {
        chips.push({
            text: "Delete Not Synced",
            detail: `${formatAgo(latestDeleteTime)}  ·  ${formatDateTimeShort(latestDeleteTime)}`,
            tone: "warning",
            title: formatDateTimeShort(latestDeleteTime),
        });
    }

    return chips;
}

export function getVersionMismatchChips(row) {
    const versions = row.Versions || [];
    const hasClientType = versions.some(v => !!v.clientType);

    return (row.VersionMismatchList || [])
        .filter(s => s.includes("VERSION MISMATCH"))
        .map(flag => {
            const isService = (flag || "").toUpperCase().startsWith("SERVICE");
            const versionEntry = versions.find(v => {
                if (hasClientType) {
                    return isService ? v.clientType === "SERVICE" : v.clientType === "POS";
                }
                return isService ? v.label !== "POS" : v.label === "POS";
            });

            const chip = {
                text: formatVersionMismatchLabel(flag),
                tone: "warning",
            };

            if (versionEntry?.running && versionEntry?.current) {
                chip.detail = `${versionEntry.running} → ${versionEntry.current}`;
                chip.title = `Running ${versionEntry.running}, latest ${versionEntry.current}`;
            }

            return chip;
        });
}

export function getServiceNotWorkingChip(row) {
    const entry = getServiceNotWorkingEntry(row);
    if (!entry) return [];

    // Tone reflects severity: danger if the whole machine is offline
    // (worse — nothing is reachable), warning if only this service's
    // heartbeat has gone stale while the machine itself is still online.
    const tone = Number(row.IsOffline) === 1 ? "danger" : "warning";

    const chip = { text: "Service Not Working", tone };

    if (entry.lastSync) {
        chip.detail = `${formatAgo(entry.lastSync)}  ·  ${formatDateTimeShort(entry.lastSync)}`;
        chip.title = formatDateTimeShort(entry.lastSync);
    }

    return [chip];
}

// Sub-tab rows are already filtered to one problem type. Keep their chips
// scoped to that same predicate so the compact card shows the active reason,
// while the modal can still show the full machine picture.


// Sort key for "Service Not Working" — bigger gap (longer since last
// heartbeat) sorts first. Rows with no lastSync timestamp (status-only
// failures with no time data) sort last, since there's nothing to rank them by.
export function getServiceNotWorkingGapMinutes(row) {
    const entry = getServiceNotWorkingEntry(row);
    if (!entry || !entry.lastSync) return 0;
    return (Date.now() - new Date(entry.lastSync).getTime()) / (1000 * 60);
}

// Sort key for "Bill Time Wise" — the worse of the invoice/delete gaps
// (whichever is more overdue) sorts first.
export function getBillTimeMismatchMaxGapMinutes(row) {
    const { invoiceGapMismatch, deleteGapMismatch, latestInvoiceTime, latestDeleteTime } =
        getBillTimeMismatchFlags(row);
    const now = Date.now();
    let maxGap = 0;

    if (invoiceGapMismatch && latestInvoiceTime) {
        maxGap = Math.max(maxGap, Math.abs(now - new Date(latestInvoiceTime).getTime()) / (1000 * 60));
    }
    if (deleteGapMismatch && latestDeleteTime) {
        maxGap = Math.max(maxGap, Math.abs(now - new Date(latestDeleteTime).getTime()) / (1000 * 60));
    }

    return maxGap;
}

// Combined sort key for the "Needs Attention" tab with no sub-tab selected
// (mixed issue types). Takes the worst of: offline duration, service
// heartbeat gap, and bill time gap — whichever is largest wins, since that's
// the most urgent thing about that machine. Rows with no time-based issue
// (e.g. only a bill-number or version mismatch) get 0 and sort after
// anything with an actual time gap.
export function getMaxIssueGapMinutes(row) {
    let maxGap = 0;

    if (Number(row.IsOffline) === 1 && row.LastSeenAt) {
        maxGap = Math.max(maxGap, (Date.now() - new Date(row.LastSeenAt).getTime()) / (1000 * 60));
    }

    maxGap = Math.max(maxGap, getServiceNotWorkingGapMinutes(row));
    maxGap = Math.max(maxGap, getBillTimeMismatchMaxGapMinutes(row));

    return maxGap;
}

// Combined Invoice chip — merges number-mismatch and not-synced info into a
// single chip (server machines only) instead of two separate ones. The
// number is only shown when it's actually present, and the headline text
// adapts to whichever issue(s) apply.
export function getInvoiceChip(row,skipNumberCheck = false) {
    if (Number(row.IsServerMachine) !== 1) return null;

    const numberMismatch = !skipNumberCheck &&
        normalizeInvoiceNo(row.MaxInvoiceFullInvoiceNo) !== normalizeInvoiceNo(row.ERPMaxInvoiceFullInvoiceNo);
    const { invoiceGapMismatch } = getBillTimeMismatchFlags(row);
    const gapMismatch = !skipNumberCheck && invoiceGapMismatch;
    if (!numberMismatch && !gapMismatch) return null;

    const posNo = row.MaxInvoiceFullInvoiceNo;
    const erpNo = row.ERPMaxInvoiceFullInvoiceNo;

    const detail =
        `POS: ${formatAgo(row.MaxInvoiceCreatedOn)}${posNo ? `(${posNo})` : ""}\n` +
        `ERP: ${formatAgo(row.ERPMaxInvoiceCreatedOn)}${erpNo ? `(${erpNo})` : ""}`;

    const text =
        numberMismatch && invoiceGapMismatch
            ? "Invoice Number Mismatch & Not Synced"
            : numberMismatch
                ? "Invoice Number Mismatch"
                : "Invoice Not Synced";

    return {
        text,
        detail,
        tone: numberMismatch ? "danger" : "warning",
        title: `POS: ${formatDateTimeShort(row.MaxInvoiceCreatedOn)}  ·  ERP: ${formatDateTimeShort(row.ERPMaxInvoiceCreatedOn)}`,
    };
}

// Same as getInvoiceChip, for the delete side.
export function getDeleteChip(row,skipNumberCheck = false) {
    if (Number(row.IsServerMachine) !== 1) return null;

    const numberMismatch = !skipNumberCheck &&
        normalizeInvoiceNo(row.MaxDeletedInvoiceFullInvoiceNo) !== normalizeInvoiceNo(row.ERPDeleteMaxInvoiceFullInvoiceNo);
    const { deleteGapMismatch } = getBillTimeMismatchFlags(row);
    const gapMismatch = !skipNumberCheck && deleteGapMismatch;
    if (!numberMismatch && !gapMismatch) return null;

    const posNo = row.MaxDeletedInvoiceFullInvoiceNo;
    const erpNo = row.ERPDeleteMaxInvoiceFullInvoiceNo;

    const detail =
        `POS: ${formatAgo(row.MaxDeletedInvoiceCreatedOn)}${posNo ? `(${posNo})` : ""}\n` +
        `ERP: ${formatAgo(row.ERPDeleteMaxInvoiceCreatedOn)}${erpNo ? `(${erpNo})` : ""}`;

    const text =
        numberMismatch && deleteGapMismatch
            ? "Delete Number Mismatch & Not Synced"
            : numberMismatch
                ? "Delete Number Mismatch"
                : "Delete Not Synced";

    return {
        text,
        detail,
        tone: numberMismatch ? "danger" : "warning",
        title: `POS: ${formatDateTimeShort(row.MaxDeletedInvoiceCreatedOn)}  ·  ERP: ${formatDateTimeShort(row.ERPDeleteMaxInvoiceCreatedOn)}`,
    };
}

export function getSubTabChips(row, subTab) {
    switch (subTab) {
        case "serviceNotWorking":
            return getServiceNotWorkingChip(row);
        case "billNumberWise":
            // number mismatch only, no time/sync info
            return getBillNumberMismatchChips(row);
        case "billTimeWise":
            // time/sync gap only, no number info
            return getBillTimeMismatchChips(row);
        case "versionMismatch":
            return getVersionMismatchChips(row);
        default:
            return [];
    }
}

